/**
 * Mock visual da NFS-e IPM — layout alinhado à referência Prefeitura de Biguaçu/SC (homologação).
 * Não é PDF oficial; apenas prévia administrativa.
 */

import React from 'react';
import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';

/** Brasão municipal: origem primária no vover-billing (/images/PMB.png) via proxy do vover-admin. */
const BRASAO_BIGUACU_SRC = '/billing/assets/pmb-logo';
const BRASAO_BIGUACU_FALLBACK_SRC =
  'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d9/Brasão_de_Biguaçu.svg/200px-Brasão_de_Biguaçu.svg.png';

export type PrestadorHomologacao = {
  razaoSocial: string;
  cnpj: string;
  logradouro: string;
  numero?: string;
  bairro: string;
  cidadeUf: string;
  cep: string;
  inscricaoMunicipal: string;
  telefone?: string;
};

export type NfCabecalhoHomologacao = {
  numeroNfse: string;
  serieNfse: string;
  situacao: string;
  tipoDocumento: string;
  dataFatoGerador: string;
  dataHoraEmissao: string;
  identificadorAutenticidade: string;
  linkConsulta: string;
  chaveAcessoNacional?: string;
};

export type TomadorHomologacao = {
  nomeRazao: string;
  cpfCnpj: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cep: string;
  cidade: string;
  uf: string;
  paisLinha: string;
  telefone: string;
  email: string;
};

export type ItemServicoHomologacao = {
  codigoListaServico: string;
  codigoLocalPrestacao: string;
  aliquotaPercent: string;
  unidade: string;
  quantidade: string;
  valorUnitario: string;
  valorServico: string;
  valorDeducao: string;
  valorIss: string;
  naturezaOperacao: string;
  descricaoServico: string;
};

export type TotaisHomologacao = {
  valorTotal: string;
  descIncondicional: string;
  valorDeducao: string;
  baseCalculo: string;
  issqn: string;
  valorLiquido: string;
  issrf: string;
  ir: string;
  inss: string;
  csll: string;
  cofins: string;
  pis: string;
  outrasRetencoes: string;
  totalTribFederais: string;
  descCondicional: string;
};

export type RodapeHomologacao = {
  legendListaServico: string;
  legendLocalPrestacao: string;
  outrasInformacoes: string;
  dataVencimentoIss?: string;
  ibptLinha?: string;
};

export type NfseBiguacuHomologacaoMockProps = {
  prestador: PrestadorHomologacao;
  nf: NfCabecalhoHomologacao;
  tomador: TomadorHomologacao;
  item: ItemServicoHomologacao;
  totais: TotaisHomologacao;
  rodape: RodapeHomologacao;
  qrCodeUrl?: string;
};

const cellSx = {
  border: '1px solid #222',
  px: 0.75,
  py: 0.5,
  fontSize: '11px',
  lineHeight: 1.35
} as const;

export const NfseBiguacuHomologacaoMock: React.FC<NfseBiguacuHomologacaoMockProps> = ({
  prestador,
  nf,
  tomador,
  item,
  totais,
  rodape,
  qrCodeUrl
}) => {
  const [logoSrc, setLogoSrc] = React.useState(BRASAO_BIGUACU_SRC);
  const [logoOk, setLogoOk] = React.useState(true);

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        color: '#111',
        border: '2px solid #111',
        maxWidth: 880,
        mx: 'auto',
        fontFamily: '"Arial", "Helvetica Neue", Helvetica, sans-serif',
        fontSize: '11px'
      }}
    >
      {/* Faixa superior: prestador | centro número+situação | QR */}
      <Box sx={{ display: 'flex', borderBottom: '2px solid #111', minHeight: 120 }}>
        <Box sx={{ flex: '1 1 38%', borderRight: '1px solid #111', p: 1 }}>
          <Typography sx={{ fontSize: '10px', fontWeight: 700, mb: 0.5 }}>
            PRESTADOR DE SERVIÇOS
          </Typography>
          <Typography sx={{ fontSize: '12px', fontWeight: 700, lineHeight: 1.25 }}>
            {prestador.razaoSocial}
          </Typography>
          <Typography sx={{ fontSize: '10px', mt: 0.5 }}>CNPJ: {prestador.cnpj}</Typography>
          <Typography sx={{ fontSize: '10px' }}>
            {prestador.logradouro}
            {prestador.numero ? `, ${prestador.numero}` : ''} — {prestador.bairro}
          </Typography>
          <Typography sx={{ fontSize: '10px' }}>
            {prestador.cidadeUf} — CEP {prestador.cep}
          </Typography>
          <Typography sx={{ fontSize: '10px' }}>
            Inscr. Municipal: {prestador.inscricaoMunicipal}
            {prestador.telefone ? ` — Tel. ${prestador.telefone}` : ''}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: '0 0 24%',
            borderRight: '1px solid #111',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box sx={{ flex: 1, borderBottom: '1px solid #111', p: 0.75, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '9px', color: '#333' }}>Número da NFS-e</Typography>
            <Typography sx={{ fontSize: '20px', fontWeight: 800 }}>{nf.numeroNfse}</Typography>
          </Box>
          <Box sx={{ p: 0.75, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '9px' }}>Situação</Typography>
              <Typography sx={{ fontSize: '9px', fontWeight: 700 }}>{nf.situacao}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '9px' }}>Tipo</Typography>
              <Typography sx={{ fontSize: '9px', fontWeight: 700 }}>{nf.tipoDocumento}</Typography>
            </Box>
            <Typography sx={{ fontSize: '8px', color: '#555', mt: 0.5 }}>
              Série {nf.serieNfse}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ flex: '1 1 38%', p: 1, textAlign: 'center' }}>
          {qrCodeUrl ? (
            <Box
              component="img"
              src={qrCodeUrl}
              alt="QR autenticidade"
              sx={{ width: 112, height: 112, imageRendering: 'pixelated' }}
            />
          ) : (
            <Box
              sx={{
                width: 112,
                height: 112,
                border: '1px dashed #999',
                mx: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '9px',
                color: 'text.secondary'
              }}
            >
              QR
            </Box>
          )}
          <Link
            href={nf.linkConsulta}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontSize: '10px' }}
          >
            Autenticidade
          </Link>
        </Box>
      </Box>

      <Box
        sx={{ bgcolor: '#e8e8e8', borderBottom: '2px solid #111', py: 0.75, textAlign: 'center' }}
      >
        <Typography sx={{ fontSize: '13px', fontWeight: 800, letterSpacing: 0.5 }}>
          Nota Fiscal de Serviço Eletrônica — Série NFS-e
        </Typography>
      </Box>

      {/* Município + identificador */}
      <Box sx={{ display: 'flex', borderBottom: '1px solid #111' }}>
        <Box
          sx={{ flex: '1 1 55%', borderRight: '1px solid #111', p: 1, display: 'flex', gap: 1.5 }}
        >
          {logoOk ? (
            <Box
              component="img"
              src={logoSrc}
              alt="Brasão Biguaçu"
              onError={() => {
                if (logoSrc !== BRASAO_BIGUACU_FALLBACK_SRC) {
                  setLogoSrc(BRASAO_BIGUACU_FALLBACK_SRC);
                  return;
                }
                setLogoOk(false);
              }}
              sx={{ width: 64, height: 80, objectFit: 'contain', flexShrink: 0 }}
            />
          ) : (
            <Box
              sx={{
                width: 64,
                height: 80,
                bgcolor: '#f0f0f0',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '8px',
                color: '#666',
                textAlign: 'center',
                px: 0.5
              }}
            >
              Logo PMB
            </Box>
          )}
          <Box>
            <Typography sx={{ fontSize: '10px', fontWeight: 700 }}>
              ESTADO DE SANTA CATARINA
            </Typography>
            <Typography sx={{ fontSize: '11px', fontWeight: 800 }}>
              PREFEITURA MUNICIPAL DE BIGUAÇU
            </Typography>
            <Typography sx={{ fontSize: '10px' }}>SECRETARIA MUNICIPAL DA RECEITA</Typography>
          </Box>
        </Box>
        <Box sx={{ flex: '1 1 45%', p: 1 }}>
          <Typography sx={{ fontSize: '9px', fontWeight: 700, mb: 0.5 }}>Identificador</Typography>
          <Typography
            sx={{
              fontSize: '10px',
              fontFamily: 'monospace',
              letterSpacing: 0.3,
              wordBreak: 'break-all'
            }}
          >
            {nf.identificadorAutenticidade}
          </Typography>
          <Box
            sx={{
              mt: 1,
              height: 36,
              bgcolor: 'repeating-linear-gradient(90deg, #111 0px, #111 2px, #fff 2px, #fff 4px)',
              opacity: 0.85
            }}
          />
          <Typography sx={{ fontSize: '8px', color: '#555', mt: 0.25 }}>
            (representação visual — código de barras)
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', borderBottom: '1px solid #111' }}>
        <Box sx={{ flex: 1, borderRight: '1px solid #111', p: 1 }}>
          <Typography sx={{ fontSize: '9px', fontWeight: 700 }}>Data Fato Gerador</Typography>
          <Typography sx={{ fontSize: '11px', fontWeight: 700 }}>{nf.dataFatoGerador}</Typography>
        </Box>
        <Box sx={{ flex: 1, p: 1 }}>
          <Typography sx={{ fontSize: '9px', fontWeight: 700 }}>Data/Hora Emissão</Typography>
          <Typography sx={{ fontSize: '11px', fontWeight: 700 }}>{nf.dataHoraEmissao}</Typography>
        </Box>
      </Box>

      {/* Tomador */}
      <Box sx={{ borderBottom: '2px solid #111', p: 1 }}>
        <Typography sx={{ fontSize: '11px', fontWeight: 800, mb: 1 }}>
          Tomador do Serviço
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          <Box>
            <Typography sx={{ fontSize: '9px', color: '#444' }}>Nome/Razão Social</Typography>
            <Typography sx={{ fontSize: '11px', fontWeight: 600 }}>{tomador.nomeRazao}</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '9px', color: '#444' }}>CPF/CNPJ</Typography>
            <Typography sx={{ fontSize: '11px', fontWeight: 600 }}>{tomador.cpfCnpj}</Typography>
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography sx={{ fontSize: '9px', color: '#444' }}>Endereço</Typography>
            <Typography sx={{ fontSize: '11px' }}>
              {tomador.logradouro}, {tomador.numero}
            </Typography>
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography sx={{ fontSize: '9px', color: '#444' }}>Complemento</Typography>
            <Typography sx={{ fontSize: '11px' }}>{tomador.complemento}</Typography>
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography sx={{ fontSize: '11px' }}>
              <strong>Bairro:</strong> {tomador.bairro} | <strong>CEP:</strong> {tomador.cep} |{' '}
              <strong>Cidade:</strong> {tomador.cidade} — {tomador.uf}
            </Typography>
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography sx={{ fontSize: '11px' }}>
              <strong>País:</strong> {tomador.paisLinha}
            </Typography>
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography sx={{ fontSize: '11px' }}>
              <strong>Telefone:</strong> {tomador.telefone} | <strong>E-mail:</strong>{' '}
              {tomador.email}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Tabela serviço */}
      <Box sx={{ borderBottom: '2px solid #111', overflowX: 'auto' }}>
        <Typography sx={{ fontSize: '11px', fontWeight: 800, p: 1, pb: 0 }}>
          Descrição dos Serviços Prestados
        </Typography>
        <Table size="small" sx={{ borderCollapse: 'collapse', minWidth: 720 }}>
          <TableHead>
            <TableRow>
              {[
                'Serviço',
                'Local',
                'Alíquota',
                'Unid.',
                'Qtd',
                'Valor unit.',
                'Valor serv.',
                'Desc. incond.',
                'Valor ded.',
                'Valor ISS',
                'Natureza',
                'Descrição do serviço'
              ].map(h => (
                <TableCell
                  key={h}
                  sx={{
                    ...cellSx,
                    bgcolor: '#f2f2f2',
                    fontWeight: 700,
                    fontSize: '8px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell sx={cellSx}>{item.codigoListaServico}</TableCell>
              <TableCell sx={cellSx}>{item.codigoLocalPrestacao}</TableCell>
              <TableCell sx={cellSx}>{item.aliquotaPercent}</TableCell>
              <TableCell sx={cellSx}>{item.unidade}</TableCell>
              <TableCell sx={cellSx}>{item.quantidade}</TableCell>
              <TableCell sx={cellSx}>{item.valorUnitario}</TableCell>
              <TableCell sx={cellSx}>{item.valorServico}</TableCell>
              <TableCell sx={cellSx}>{totais.descIncondicional}</TableCell>
              <TableCell sx={cellSx}>{item.valorDeducao}</TableCell>
              <TableCell sx={cellSx}>{item.valorIss}</TableCell>
              <TableCell sx={{ ...cellSx, maxWidth: 100 }}>{item.naturezaOperacao}</TableCell>
              <TableCell sx={{ ...cellSx, minWidth: 200, fontWeight: 600 }}>
                {item.descricaoServico}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      {/* Totais 5×3 */}
      <Box sx={{ p: 1, borderBottom: '2px solid #111' }}>
        <Typography sx={{ fontSize: '10px', fontWeight: 800, mb: 0.5 }}>Valores</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)' }}>
          {[
            ['Valor Total', totais.valorTotal],
            ['Desc. Incondicional', totais.descIncondicional],
            ['Dedução', totais.valorDeducao],
            ['Base de Cálculo', totais.baseCalculo],
            ['ISSQN', totais.issqn],
            ['ISSRF', totais.issrf],
            ['IR', totais.ir],
            ['INSS', totais.inss],
            ['CSLL', totais.csll],
            ['COFINS', totais.cofins],
            ['PIS', totais.pis],
            ['Outras Retenções', totais.outrasRetencoes],
            ['Total Trib. Federais', totais.totalTribFederais],
            ['Desc. Condicional', totais.descCondicional],
            ['Valor Líquido', totais.valorLiquido]
          ].map(([k, v]) => (
            <Box
              key={String(k)}
              sx={{
                border: '1px solid #111',
                p: 0.5,
                fontSize: '9px'
              }}
            >
              <Typography sx={{ fontSize: '8px', color: '#444' }}>{k}</Typography>
              <Typography sx={{ fontWeight: 700 }}>{v}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Rodapé */}
      <Box sx={{ p: 1.5, fontSize: '9px', lineHeight: 1.45 }}>
        <Typography sx={{ mb: 0.5 }}>
          <strong>Subitem:</strong> {rodape.legendListaServico}
        </Typography>
        <Typography sx={{ mb: 0.5 }}>
          <strong>Legenda do Local:</strong> {rodape.legendLocalPrestacao}
        </Typography>
        <Typography sx={{ mb: 1, textAlign: 'justify' }}>
          <strong>Outras informações:</strong> {rodape.outrasInformacoes}
        </Typography>
        {rodape.dataVencimentoIss && (
          <Typography sx={{ mb: 0.5 }}>
            <strong>Data limite pagamento ISS:</strong> {rodape.dataVencimentoIss}
          </Typography>
        )}
        {rodape.ibptLinha && (
          <Typography sx={{ mb: 0.5, fontSize: '8px' }}>{rodape.ibptLinha}</Typography>
        )}
        {nf.chaveAcessoNacional && (
          <Typography
            sx={{ mt: 1, fontFamily: 'monospace', fontSize: '10px', wordBreak: 'break-all' }}
          >
            <strong>Chave de Acesso NFS-e Nacional:</strong> {nf.chaveAcessoNacional}
          </Typography>
        )}
        <Typography sx={{ mt: 1, fontSize: '8px', color: '#666' }}>
          Prévia de homologação — layout baseado na emissão IPM Biguaçu/SC. Valores finais são
          gerados pela prefeitura após autorização.
        </Typography>
      </Box>
    </Box>
  );
};
